import { defineComponent, h } from 'vue';
import { TagField, TagFieldProps, tagFieldProps } from '@/ui/components/form';
import { SetterCallback } from '@/ui/types';
import { PermissionOption, permissionsGrouped } from '@/ui/dictionary';
import { GroupOptions } from '@/ui/components/controls/TagInput';
import { featureExplanationIcon } from '@/ui/components/icon/icons';
import { tooltip } from '@/ui/components/Tooltip';
import { t } from '@/ui/i18n/i18n';
import { approvalsEnabledForOrganization, Permissions } from '@/ui/auth/auth';
import { isAnalyticsEnabled } from '@/ui/helpers/featureHelper';

type PermissionsDropdownProps<T> = TagFieldProps<T>;

export const PermissionsDropdown = defineComponent<PermissionsDropdownProps<number>>({
  name: 'PermissionsDropdown',
  props: tagFieldProps,
  setup(props, context) {
    const onInput = (values: unknown[]) => {
      if (values.some(value => value === undefined)) {
        return;
      }

      context.emit('input', values);
    };

    // TODO: This checking/filtering must be removed when analytics feature is fully rolled out
    let filteredPermissions = isAnalyticsEnabled()
      ? permissionsGrouped()
      : permissionsGrouped()
        .filter(permission => ![
          Permissions.MANAGE_OWN_ANALYTICS,
          Permissions.MANAGE_ALL_ANALYTICS,
        ].includes(permission.value as Permissions));

    if (!approvalsEnabledForOrganization.value) {
      filteredPermissions = filteredPermissions
        .filter(permission => ![
          Permissions.APPROVE_ALL_INSPECTIONS,
          Permissions.APPROVE_ASSIGNED_INSPECTIONS,
        ].includes(permission.value as Permissions));
    }

    const options = filteredPermissions.reduce(
      // @ts-ignore
      ((previousValue: GroupOptions<PermissionOption>[], currentValue) => {
        const groupIndex = previousValue.findIndex(option => option.type === currentValue.group);

        if (groupIndex !== -1) {
          previousValue[groupIndex].items.push(currentValue);
        } else {
          previousValue.push({ type: currentValue.group, items: [currentValue] });
        }

        return previousValue;
      }),
      [],
    );

    return function render() {
      return h(TagField, {
        class: 'permissions-dropdown',
        props: {
          ...props,
          options,
          groupField: 'type',
          groupOptions: 'items',
        },
        on: { input: onInput },
        scopedSlots: {
          default: (data: { option?: PermissionOption }) => {
            if (data.option) {
              return h('div', { class: 'flex is-align-items-center' }, [
                data.option.label,
                tooltip({
                  label: t(`permissions.description.${data.option.value}`),
                  multilined: true,
                  position: 'is-right',
                  appendToBody: true,
                }, [featureExplanationIcon({ classes: 'ml-2' })]),
              ]);
            }

            return null;
          },
        },
      });
    };
  },
});

export function permissionsDropdown<T>(
  props: Omit<PermissionsDropdownProps<T>, 'options'>,
  input: SetterCallback<T[]>,
) {
  return h(PermissionsDropdown, { props, on: { input } });
}
